/**
 * The words the review screen says, in one place.
 *
 * Separate from the component because these strings are the feature: a verdict
 * a casual player cannot read is a verdict that did not happen. They are also
 * the thing worth asserting in a test, and a test should not have to render a
 * board to check a sentence.
 */
import type { MoveFact } from '../../../games/analysis/reasons.ts';
import type { MoveReview, SeatTally, Verdict } from '../../../games/analysis/review.ts';
import { MATE_THRESHOLD, mateDistance } from '../../../games/analysis/search.ts';

/** The word on the chip, and how loudly it is drawn. */
export interface VerdictStyle {
  readonly label: string;
  /**
   * 0 to 3. Drives the chip's severity tick — a run of bars whose *length and
   * weight* carry the same message as the colour, so the verdict survives
   * greyscale, forced colours and a compressed stream (WCAG 1.4.1).
   */
  readonly severity: 0 | 1 | 2 | 3;
}

const VERDICT_STYLES: Record<Verdict, VerdictStyle> = {
  best: { label: 'Best move', severity: 0 },
  only: { label: 'Only move', severity: 0 },
  good: { label: 'Good', severity: 0 },
  inaccuracy: { label: 'Inaccuracy', severity: 1 },
  mistake: { label: 'Mistake', severity: 2 },
  blunder: { label: 'Blunder', severity: 3 },
};

export function verdictStyle(verdict: Verdict): VerdictStyle {
  return VERDICT_STYLES[verdict];
}

/** Columns are 1-based when spoken: nobody calls the first column "column 0". */
export function columnName(param: number): string {
  return `column ${String(param + 1)}`;
}

/**
 * Plies converted into the winner's own moves.
 *
 * "Wins in 6" is meaningless to someone who does not think in plies; "wins in 3
 * moves" is the number of discs they still have to drop.
 */
export function winsInMoves(plies: number, seatCount: number): number {
  return Math.max(1, Math.ceil(plies / Math.max(1, seatCount)));
}

/**
 * How the position stands after a move, in one short phrase.
 *
 * A proven result is stated as a proven result. Anything else is deliberately
 * vague — "ahead" rather than a number — because a heuristic score dressed up
 * as a measurement is a lie with a decimal point.
 */
export function assessment(
  entry: MoveReview,
  nameOf: (seat: number) => string,
  seats: readonly number[],
): string {
  const seatCount = seats.length;
  // `playedScore` is from the mover's point of view; flip it to talk about the
  // seat that is actually doing well.
  const distance = mateDistance(entry.playedScore);
  if (distance !== null) {
    const winner =
      entry.playedScore > 0 ? entry.seat : (seats.find((seat) => seat !== entry.seat) ?? entry.seat);
    return `${nameOf(winner)} wins in ${String(winsInMoves(distance, seatCount))}`;
  }
  if (entry.playedScore === 0) return 'Level';

  const leader =
    entry.playedScore > 0 ? entry.seat : (seats.find((seat) => seat !== entry.seat) ?? entry.seat);
  const margin = Math.abs(entry.playedScore);
  const strength = margin > 300 ? 'is well ahead' : margin > 80 ? 'is ahead' : 'is a little ahead';
  return `${nameOf(leader)} ${strength}`;
}

/**
 * Where the eval bar's divider sits, as a percentage owned by `seat`.
 *
 * Proven results pin to the ends; everything else is squashed through a curve
 * so that ordinary middlegame swings are visible without a single tactic
 * slamming the bar to one side.
 */
export function evalShare(entry: MoveReview, seat: number): number {
  const fromSeat = entry.seat === seat ? entry.playedScore : -entry.playedScore;
  if (fromSeat >= MATE_THRESHOLD) return 100;
  if (fromSeat <= -MATE_THRESHOLD) return 0;
  const curved = Math.tanh(fromSeat / 400);
  return Math.round((50 + curved * 50) * 10) / 10;
}

/**
 * What the live region says when the ply changes.
 *
 * One sentence, in playing order: which move, who played it, what it was worth,
 * what the engine wanted instead, and why. It is the whole screen read aloud,
 * so somebody who cannot see the ghost disc still gets the answer — including
 * the reason, which would otherwise be the one part of the screen only sighted
 * users get.
 */
export function announcement(
  input: {
    readonly index: number;
    readonly total: number;
    readonly param: number;
    readonly seat: number;
    readonly entry: MoveReview | null;
    readonly analysing: boolean;
    readonly analysedCount: number;
  },
  nameOf: (seat: number) => string,
  seats: readonly number[],
): string {
  const head = `Move ${String(input.index + 1)} of ${String(input.total)}, ${nameOf(input.seat)} played ${columnName(input.param)}`;

  if (input.entry === null) {
    return input.analysing
      ? `${head}. Analysing ${String(input.analysedCount)} of ${String(input.total)}.`
      : `${head}.`;
  }

  const style = verdictStyle(input.entry.verdict);
  const why = reasonSentences(input.entry, nameOf, seats).join(' ');
  const tail = why === '' ? '' : ` ${why}`;

  if (input.entry.bestParam === input.entry.param) {
    return `${head}. ${style.label}. ${assessment(input.entry, nameOf, seats)}.${tail}`;
  }

  const best = columnName(input.entry.bestParam);
  const bestDistance = mateDistance(input.entry.bestScore);
  const better =
    bestDistance !== null && input.entry.bestScore > 0
      ? `${best} would have won in ${String(winsInMoves(bestDistance, seats.length))}`
      : `${best} was better`;
  return `${head}. ${style.label}. ${better}.${tail}`;
}

/** The seat that is not `seat`. Two-seat games are the only ones the verdicts score. */
function opponentOf(seat: number, seats: readonly number[]): number {
  return seats.find((candidate) => candidate !== seat) ?? seat;
}

function firstFact<K extends MoveFact['kind']>(
  facts: readonly MoveFact[],
  kind: K,
): Extract<MoveFact, { kind: K }> | null {
  for (const fact of facts) {
    if (fact.kind === kind) return fact as Extract<MoveFact, { kind: K }>;
  }
  return null;
}

/**
 * Why the move on screen was good or bad, in plain sentences.
 *
 * Two at most, and only when they were proven by replaying the board — the
 * engine's score can rank a move but cannot explain one, so a position with
 * nothing tactical to say gets silence rather than filler. Silence is a real
 * outcome here: most opening moves are simply reasonable.
 *
 * The order is the order a player reads their own move in: what it cost first,
 * then what the better move was for.
 */
export function reasonSentences(
  entry: MoveReview,
  nameOf: (seat: number) => string,
  seats: readonly number[],
): readonly string[] {
  const them = nameOf(opponentOf(entry.seat, seats));
  const { played, best, missedWin } = entry.reasoning;
  const sentences: string[] = [];

  // A win that was sitting there outranks everything else the screen could say.
  if (missedWin) {
    sentences.push(`${capitalise(columnName(entry.bestParam))} would have won on the spot.`);
  }

  const gaveDouble = firstFact(played, 'allows_double_threat');
  const gaveWin = firstFact(played, 'allows_win');
  const blocked = firstFact(best, 'blocks_win');

  /*
   * The commonest loss in the game: a threat was already on the board and the
   * move played was not the answer to it.
   *
   * Said as one sentence, because "it let them win at column 4" followed by
   * "column 4 would have blocked their line" is one fact told twice — and the
   * repetition reads as two separate errors to someone still stinging from the
   * loss.
   */
  if (gaveWin !== null && blocked !== null && gaveWin.param === blocked.param) {
    sentences.push(
      `${them} was one move from a line — ${columnName(gaveWin.param)} was the square that stopped it.`,
    );
    return sentences.slice(0, 2);
  }

  // What the played move handed over.
  if (gaveDouble !== null) {
    sentences.push(
      `It gave ${them} two ways to win — ${listColumns(gaveDouble.params)} — and only one can be blocked.`,
    );
  } else if (gaveWin !== null) {
    sentences.push(`It let ${them} win by playing ${columnName(gaveWin.param)}.`);
  }

  /*
   * What the move on the board achieved, when it was the engine's own choice.
   * This is the half that turns "Best move" from a gold star into a lesson.
   *
   * Skipped entirely once the move handed a win over: the opponent plays next,
   * so a threat of your own is moot and "only one can be blocked" would be a
   * false promise — they never have to block either.
   */
  const handedOver = gaveDouble !== null || gaveWin !== null;
  if (best.length === 0) {
    const won = firstFact(played, 'wins_now');
    const blockedByPlayed = firstFact(played, 'blocks_win');
    const forked = firstFact(played, 'makes_double_threat');
    const threatened = firstFact(played, 'makes_threat');

    if (won !== null) sentences.push('It completed the line.');
    else if (handedOver) {
      // Nothing to add: the sentence above already says how the game is lost.
    } else if (forked !== null) {
      sentences.push(
        `It made two ways to win — ${listColumns(forked.params)} — and only one can be blocked.`,
      );
    } else if (blockedByPlayed !== null) sentences.push(`It blocked ${them}'s line.`);
    else if (threatened !== null) {
      sentences.push(`It threatens ${columnName(threatened.param)} next, so ${them} has to answer.`);
    }

    // A quiet move still did something. Without this a "Good" verdict is a gold
    // star with no lesson attached — which is the one case a learner most needs
    // explained, because nothing on the board tells them what they got right.
    if (sentences.length === 0) sentences.push(...positionalSentences(played));
    return sentences.slice(0, 2);
  }

  // What the engine's move would have done instead.
  const better = capitalise(columnName(entry.bestParam));
  const bestWins = firstFact(best, 'wins_now');
  const bestForks = firstFact(best, 'makes_double_threat');
  const bestThreatens = firstFact(best, 'makes_threat');

  if (bestWins !== null && !missedWin) sentences.push(`${better} would have won on the spot.`);
  else if (blocked !== null) sentences.push(`${better} would have blocked ${them}'s line.`);
  else if (bestForks !== null) {
    sentences.push(
      `${better} would have made two ways to win — ${listColumns(bestForks.params)} — and only one can be blocked.`,
    );
  } else if (bestThreatens !== null) {
    sentences.push(`${better} would have forced ${them} to answer.`);
  }

  /*
   * Neither move had a tactic to describe.
   *
   * Describe the *better* move rather than the played one: the engine disagreed
   * with what was played, so praising it ("It built a run of 2…") under a
   * Mistake chip would be the screen contradicting itself. What the player
   * wants here is the thing they did not see.
   */
  if (sentences.length === 0) {
    /*
     * Only the concrete facts get re-pointed. A reach *comparison* is about the
     * square someone chose against the ones they passed over, so aiming it at
     * the engine's move produces a sentence that argues with itself — "Column 3
     * would have been in 5 winning lines; the best you could reach was in 7"
     * recommends a move and undercuts it in the same breath. Seen on
     * production, not in review.
     */
    const missed = positionalSentences(best).filter(
      (sentence) => !/^That square is in/.test(sentence),
    );
    if (missed.length > 0) sentences.push(`${better} would have ${lowerVerb(missed[0] ?? '')}`);
    else sentences.push(...positionalSentences(played));
  }

  return sentences.slice(0, 2);
}

/**
 * Re-points a sentence about a played move at a move that was not played.
 *
 * "It took a square 3 of their lines needed." becomes "...would have taken a
 * square 3 of their lines needed." Written as a small table rather than as
 * string surgery, so a new sentence cannot silently produce broken grammar —
 * it simply does not get re-pointed.
 */
function lowerVerb(sentence: string): string {
  const rewrites: readonly (readonly [RegExp, string])[] = [
    [/^It took the busiest square going/, 'taken the busiest square going'],
    [/^It took a square/, 'taken a square'],
    [/^It made (\d+) in a row/, 'made $1 in a row'],
    [/^It built a run of (\d+)/, 'built a run of $1'],
    [/^That square is in/, 'been in'],
  ];
  for (const [pattern, replacement] of rewrites) {
    if (pattern.test(sentence)) return sentence.replace(pattern, replacement);
  }
  return sentence;
}

/**
 * What a quiet move did, in the order a player can act on next game.
 *
 * Every number here is a tally over the winning lines the spec declares, so the
 * sentence is checkable on the board rather than a compliment. At most two, and
 * still nothing at all when the move genuinely did nothing countable — the
 * first piece on an empty board being the honest example.
 */
function positionalSentences(facts: readonly MoveFact[]): readonly string[] {
  const builds = firstFact(facts, 'builds_line');
  const denies = firstFact(facts, 'denies_lines');
  const reach = firstFact(facts, 'square_reach');
  const out: string[] = [];

  if (builds !== null) {
    const need = builds.length - builds.count;
    out.push(
      need === 1
        ? `It made ${String(builds.count)} in a row with room to finish — one more completes it.`
        : `It built a run of ${String(builds.count)} in a line still open to finish.`,
    );
  }

  if (denies !== null) {
    out.push(
      denies.count === 1
        ? 'It took a square one of their lines needed.'
        : `It took a square ${String(denies.count)} of their lines needed.`,
    );
  }

  /*
   * The square's reach, which every move has.
   *
   * Only worth saying on its own: beside a real run it is the weaker fact, and
   * two sentences is the cap. Said as a comparison rather than a bare number,
   * because "3 winning lines" means nothing without the 7 it is being measured
   * against — and that comparison *is* the opening lesson.
   */
  if (out.length === 0 && reach !== null) {
    out.push(
      reach.lines >= reach.bestAvailable
        ? `It took the busiest square going — ${String(reach.lines)} winning lines run through it.`
        : `That square is in ${String(reach.lines)} winning lines; the best you could reach was in ${String(reach.bestAvailable)}.`,
    );
  }

  return out.slice(0, 2);
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "column 2 and column 5", or "columns 2, 5 and 6" once there are more than two. */
function listColumns(params: readonly number[]): string {
  const names = params.map((param) => String(param + 1));
  if (names.length === 0) return '';
  if (names.length === 1) return `column ${String(names[0])}`;
  const last = names[names.length - 1] ?? '';
  return `columns ${names.slice(0, -1).join(', ')} and ${last}`;
}

/** "matched the engine on 9 of 19 moves, 1 blunder" — countable, not a grade. */
export function tallySentence(tally: SeatTally, name: string): string {
  const errors: string[] = [];
  if (tally.inaccuracies > 0)
    errors.push(`${String(tally.inaccuracies)} inaccurac${tally.inaccuracies === 1 ? 'y' : 'ies'}`);
  if (tally.mistakes > 0)
    errors.push(`${String(tally.mistakes)} mistake${tally.mistakes === 1 ? '' : 's'}`);
  if (tally.blunders > 0)
    errors.push(`${String(tally.blunders)} blunder${tally.blunders === 1 ? '' : 's'}`);

  const matched = `${name} matched the engine on ${String(tally.matched)} of ${String(tally.moves)} moves`;
  return errors.length === 0 ? `${matched}.` : `${matched}, ${errors.join(', ')}.`;
}
